import { Grid, styled, Typography } from '@mui/material'
import { FC } from 'react'
import { useShowContext, useTranslate } from 'react-admin'
import { xxllncColor } from '../../layout/themes'
import { CertificateOut, CertificateStatus, CertificateType, TargetSystem } from '../../types/apiRequests'
import CertificateLogs from './CertificateLogs'
import StatusChip from '../../components/StatusChip'
import CertificateSchedules from './CertificateSchedules'
import ActionsByStatus from './CertificateActionsByStatus'
import ExpiryAlert from './CertificateExpiryAlert'
import AddressDetails from './details/Address.details'
import ZakenDetails from './details/Zaken.details'
import { getCommonName } from '../../utils/getCommonName'
import { getCapabilities } from './targetSystems'


const CertificateForm: FC = () => {
  const translate = useTranslate()
  const { record } = useShowContext<CertificateOut>()

  const certificateIsexpired = record?.verloopDatum ? new Date(record.verloopDatum) < new Date() : false

  return (
    <StyledGrid container spacing={4} className={classes.root}>

      { record?.status === CertificateStatus.ACTIVE ||
        record?.status === CertificateStatus.EXPIRED ||
        certificateIsexpired ? (
          <Grid item sm={12} >
            <ExpiryAlert />
          </Grid>
        ) : null }

      <Grid item lg={8} md={6} sm={12}>
        {/* Bezwaren/Koppelen have no installation, so no schedule section. */}
        {getCapabilities(record?.targetSystem)?.installable && (
          <CertificateSchedules certificateId={record?.id} />
        )}
        <CertificateLogs record={record} />
      </Grid>

      <Grid item lg={4} md={6} sm={12} >
        <Typography variant="subtitle1" sx={{ marginBottom: '10px' }}>
          <span>{translate('resources.certificates.fields.contactDetails')}:</span>
        </Typography>
        <AddressDetails value={record?.contactNaam || '-'} />
        <AddressDetails value={record?.contactEmail || '-'} />
        <AddressDetails value={record?.contactTelefoon || '-'} />
        <br />

        <Typography variant="subtitle1" sx={{ marginBottom: '10px' }}>
          <span>{translate('data.status')}</span>
        </Typography>
        <StatusChip />
        <br />

        {/* Zaken details include the hostname; other systems only have the hostname. */}
        { record?.targetSystem === TargetSystem.ZAKEN ? <ZakenDetails /> : (
          <AddressDetails
            title={translate('resources.certificates.fields.host')}
            value={getCommonName(record) || '-'}
          />
        ) }

        <br />
        <Typography variant="subtitle1" sx={{ marginTop: '20px', marginBottom: '10px' }}>
          <span>{translate('data.actions')}</span>
        </Typography>
        <ActionsByStatus />

      </Grid>
    </StyledGrid>
  )
}

const PREFIX = 'xxllncTicketsForm'

const classes = {
  root: `${PREFIX}-root`,
  item: `${PREFIX}-item`,
  info: `${PREFIX}-info`,
  comments: `${PREFIX}-comments`,
  commenRow: `${PREFIX}-commenRow`,
}

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    [theme.breakpoints.down('md')]: {
      margin: '0 20px',
      width: 'calc(100% - 35px)',
    }
  },
  [`& .${classes.item}`]: {
    paddingLeft: 0,
  },
  [`& .${classes.info}`]: {
    display: 'flex',
    flexDirection: 'row',
    width: '100%',
    borderBottom: `1px solid ${xxllncColor.dark20}`,
    paddingBottom: '24px',
    marginBottom: '30px',
    marginTop: '16px',
    ['& span']: {
      fontSize: '10pt',
      color: '#6E6E74',
      paddingRight: '20px',
      marginRight: '20px',
      ['&:not(:last-child)']: {
        borderRight: `1px solid ${xxllncColor.dark20}`,
      },
    },
  },
  [`& .${classes.comments}`]: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  [`& .${classes.commenRow}`]: {
    display: 'flex',
    flexDirection: 'row',
    marginBottom: '5px',
    padding: '16px',
  },
}))

export default CertificateForm
